import React, { useState } from 'react';
import FieldMappingPicker from './FieldMappingPicker';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Dropdown (Select)' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'file', label: 'File Upload' },
];

function parseFields(value) {
  if (Array.isArray(value)) return value.filter((f) => f && typeof f === 'object');
  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return [];
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p.filter((f) => f && typeof f === 'object');
    } catch {}
  }
  return [];
}

const inputSm = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  marginBottom: '8px',
};

const labelSm = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 'bold',
  color: 'var(--text-secondary)',
  marginBottom: '3px',
};

const rowStyle = { display: 'flex', gap: '10px' };

const btnStyle = {
  padding: '4px 8px',
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

export default function FormBuilder({ value, onChange, fieldCatalog }) {
  const fields = parseFields(value);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const update = (nextFields) => onChange(nextFields);

  const addField = () => {
    const next = [
      ...fields,
      {
        name: `field_${fields.length + 1}`,
        label: '',
        type: 'text',
        required: false,
        placeholder: '',
      },
    ];
    update(next);
    setExpandedIndex(next.length - 1);
  };

  const removeField = (i) => {
    update(fields.filter((_, idx) => idx !== i));
    setExpandedIndex((prev) => (prev === i ? null : prev !== null && prev > i ? prev - 1 : prev));
  };

  const updateField = (i, patch) => {
    update(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  };

  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
    setExpandedIndex(j);
  };

  const renderPreview = () => (
    <div
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        padding: '16px',
        marginTop: '8px',
        background: 'var(--bg-secondary)',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: 'bold',
          color: 'var(--text-tertiary)',
          marginBottom: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Form Preview
      </div>
      {fields.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>No fields added yet.</div>
      ) : (
        fields.map((f, i) => (
          <div key={i} style={{ marginBottom: '14px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '4px',
                color: 'var(--text-primary)',
              }}
            >
              {f.label || f.name || `Field ${i + 1}`}
              {f.required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                disabled
                placeholder={f.placeholder || ''}
                style={{ ...inputSm, minHeight: '60px', resize: 'vertical', opacity: 0.6 }}
              />
            ) : f.type === 'select' ? (
              <select disabled style={{ ...inputSm, opacity: 0.6 }}>
                <option>— Select —</option>
                {(f.options || []).map((o, j) => (
                  <option key={j}>{o}</option>
                ))}
              </select>
            ) : f.type === 'checkbox' ? (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  cursor: 'default',
                }}
              >
                <input type="checkbox" disabled readOnly />
                {f.label || f.name}
              </label>
            ) : (
              <input
                type={
                  f.type === 'date'
                    ? 'date'
                    : f.type === 'email'
                    ? 'email'
                    : f.type === 'number'
                    ? 'number'
                    : 'text'
                }
                disabled
                placeholder={f.placeholder || ''}
                style={{ ...inputSm, opacity: 0.6 }}
              />
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <div style={{ marginBottom: '15px' }}>
      {fields.map((field, i) => {
        const isExpanded = expandedIndex === i;
        const typeLabel = FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type;
        return (
          <div
            key={i}
            style={{
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              marginBottom: '6px',
              overflow: 'hidden',
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 10px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={() => setExpandedIndex(isExpanded ? null : i)}
            >
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', minWidth: '14px' }}>
                {isExpanded ? '▼' : '▶'}
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: '13px',
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {field.label || field.name || `Field ${i + 1}`}
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '5px', fontWeight: 400 }}>
                  {typeLabel}
                  {field.required ? ' · required' : ''}
                </span>
              </span>
              <button
                style={{ ...btnStyle, padding: '2px 6px' }}
                onClick={(e) => { e.stopPropagation(); moveField(i, -1); }}
                disabled={i === 0}
                title="Move up"
              >↑</button>
              <button
                style={{ ...btnStyle, padding: '2px 6px' }}
                onClick={(e) => { e.stopPropagation(); moveField(i, 1); }}
                disabled={i === fields.length - 1}
                title="Move down"
              >↓</button>
              <button
                style={{ ...btnStyle, padding: '2px 6px', color: '#ef4444', borderColor: 'transparent', background: 'transparent' }}
                onClick={(e) => { e.stopPropagation(); removeField(i); }}
                title="Remove field"
              >×</button>
            </div>

            {/* Expanded editor */}
            {isExpanded && (
              <div style={{ padding: '12px', borderTop: '1px solid var(--border-color)' }}>
                <div style={rowStyle}>
                  <div style={{ flex: 1 }}>
                    <label style={labelSm}>Field ID</label>
                    <input
                      style={inputSm}
                      value={field.name || ''}
                      onChange={(e) => updateField(i, { name: e.target.value.replace(/\s+/g, '_') })}
                      placeholder="field_name"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelSm}>Label</label>
                    <input
                      style={inputSm}
                      value={field.label || ''}
                      onChange={(e) => updateField(i, { label: e.target.value })}
                      placeholder="Display label"
                    />
                  </div>
                </div>

                <FieldMappingPicker
                  catalog={fieldCatalog}
                  caseKey={field.case_field_key}
                  taskKey={field.task_field_key}
                  fieldName={field.name}
                  fieldType={field.type}
                  onChange={({ caseKey, taskKey, unmapped }) => updateField(i, { case_field_key: caseKey, task_field_key: taskKey, no_writeback: unmapped ? true : undefined })}
                />

                <div style={rowStyle}>
                  <div style={{ flex: 1 }}>
                    <label style={labelSm}>Type</label>
                    <select
                      style={{ ...inputSm }}
                      value={field.type || 'text'}
                      onChange={(e) => updateField(i, { type: e.target.value })}
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'flex-end',
                      paddingBottom: '8px',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!field.required}
                        onChange={(e) => updateField(i, { required: e.target.checked })}
                      />
                      Required
                    </label>
                  </div>
                </div>

                {/* Placeholder — not for checkbox or select */}
                {field.type !== 'checkbox' && field.type !== 'select' && field.type !== 'file' && (
                  <div>
                    <label style={labelSm}>Placeholder</label>
                    <input
                      style={inputSm}
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(i, { placeholder: e.target.value })}
                      placeholder="Hint text shown in empty field…"
                    />
                  </div>
                )}

                {/* Options editor — for select */}
                {field.type === 'select' && (
                  <div>
                    <label style={labelSm}>
                      Options{' '}
                      <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>
                        (one per line)
                      </span>
                    </label>
                    <textarea
                      style={{ ...inputSm, minHeight: '80px', resize: 'vertical' }}
                      value={(field.options || []).join('\n')}
                      onChange={(e) =>
                        updateField(i, {
                          options: e.target.value
                            .split('\n')
                            .map((s) => s.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder={'Option A\nOption B\nOption C'}
                    />
                  </div>
                )}

                {/* Min / Max — for number */}
                {field.type === 'number' && (
                  <div style={rowStyle}>
                    <div style={{ flex: 1 }}>
                      <label style={labelSm}>Min</label>
                      <input
                        style={inputSm}
                        type="number"
                        value={field.min ?? ''}
                        onChange={(e) =>
                          updateField(i, {
                            min: e.target.value === '' ? undefined : Number(e.target.value),
                          })
                        }
                        placeholder="—"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelSm}>Max</label>
                      <input
                        style={inputSm}
                        type="number"
                        value={field.max ?? ''}
                        onChange={(e) =>
                          updateField(i, {
                            max: e.target.value === '' ? undefined : Number(e.target.value),
                          })
                        }
                        placeholder="—"
                      />
                    </div>
                  </div>
                )}

                {/* Min / Max length — for text, textarea, email */}
                {(field.type === 'text' ||
                  field.type === 'textarea' ||
                  field.type === 'email') && (
                  <div style={rowStyle}>
                    <div style={{ flex: 1 }}>
                      <label style={labelSm}>Min Length</label>
                      <input
                        style={inputSm}
                        type="number"
                        min="0"
                        value={field.min_length ?? ''}
                        onChange={(e) =>
                          updateField(i, {
                            min_length:
                              e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                          })
                        }
                        placeholder="—"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelSm}>Max Length</label>
                      <input
                        style={inputSm}
                        type="number"
                        min="0"
                        value={field.max_length ?? ''}
                        onChange={(e) =>
                          updateField(i, {
                            max_length:
                              e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                          })
                        }
                        placeholder="—"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        style={{
          padding: '8px 14px',
          background: 'var(--accent-color)',
          border: 'none',
          color: 'white',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '13px',
          width: '100%',
          marginTop: fields.length > 0 ? '4px' : '0',
        }}
        onClick={addField}
      >
        + Add Field
      </button>

      {/* Live preview toggle */}
      <div style={{ marginTop: '8px' }}>
        <button
          style={{
            ...btnStyle,
            width: '100%',
            padding: '6px',
            fontSize: '12px',
            textAlign: 'center',
          }}
          onClick={() => setShowPreview((p) => !p)}
        >
          {showPreview ? '▲ Hide Preview' : '▼ Show Form Preview'}
        </button>
        {showPreview && renderPreview()}
      </div>
    </div>
  );
}
